import {AppUrls} from 'shared/config/model/urls';

import React from 'react';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import LinkButton from 'shared/ui/Button/LinkButton/LinkButton';
import MainModal from 'shared/ui/Modal/MainModal';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import IServerIncident from 'features/server/model/ServerIncident/IServerIncident';
import {getFormattedDate, secondsToFormattedString} from 'shared/lib/datetime';
import {isTranslate} from 'shared/lib/lang';
import {useServerIncidentSummary} from 'features/server/model/useServerIncidentSummary';
import {getServerIncidentThreshold} from 'features/server/lib/serverIncidentHelper';
import SuccessfulMonitor from 'shared/ui/icons/SuccessfulMonitor';
import ProblemMonitor from 'shared/ui/icons/ProblemMonitor';

import ServerIncidentStatusBadge from '../ServerIncidentStatusBadge/ServerIncidentStatusBadge';
import ServerIncidentSeverityBadge from '../ServerIncidentSeverityBadge/ServerIncidentSeverityBadge';

import styles from './ServerIncidentModal.module.scss';

interface IServerIncidentModalProps {
    open: boolean,
    item: IServerIncident,
    handleClose: () => void,
}

const ServerIncidentModal: React.FC<IServerIncidentModalProps> = ({
    open,
    item,
    handleClose,
}) => {
    const {
        isOpen, isHeartbeat, metricLabel, serverUuid, serverName, durationSeconds,
    } = useServerIncidentSummary(item);

    const dateFormat = !isTranslate() ? 'DD.MM.YYYY, в HH:mm' : 'YYYY-MM-DD, in HH:mm';

    const items: {label: string, value: React.ReactNode}[] = [
        {label: t`Важность`, value: <ServerIncidentSeverityBadge severity={item.severity}/>},
        {label: t`Условие`, value: `${metricLabel} ${getServerIncidentThreshold(item)}`},
        ...(isHeartbeat ? [] : [
            {label: t`Значение при открытии`, value: item.value_at_open ?? '—'},
            {label: t`Максимальное значение`, value: item.value_max ?? '—'},
            {label: t`Значение при закрытии`, value: item.value_at_close ?? '—'},
        ]),
    ];

    return (
        <MainModal
            dialogClassName={isOpen ? styles.backgroundModal : undefined}
            headerClassName={styles.header}
            header={
                <div>
                    <div className={styles.contentHeader}>
                        {t`Инцидент сервера`}
                        <span className={styles.title}> - {serverName}</span>
                    </div>
                    <div className={styles.contentInfo}>
                        <div className={styles.serverNameMobile}>
                            <span>{t`Сервер`}</span>
                            <span>{serverName}</span>
                        </div>
                        <div className={styles.itemInfo}>
                            <span className={styles.title}>{t`Статус`}</span>
                            <div className={styles.containerStatus}>
                                {isOpen ? <ProblemMonitor/> : <SuccessfulMonitor/>}
                                <ServerIncidentStatusBadge isOpen={isOpen}/>
                            </div>
                        </div>
                        <div className={styles.itemInfo}>
                            <span className={styles.title}>{t`Открыт`}</span>
                            <div className={styles.data}>
                                {getFormattedDate(item.opened_at, {format: dateFormat})}
                            </div>
                        </div>
                        <div className={styles.itemInfo}>
                            <span className={styles.title}>{t`Длительность`}</span>
                            <div className={styles.data}>
                                {secondsToFormattedString(durationSeconds)}
                            </div>
                        </div>
                        <div className={styles.itemInfo}>
                            <span className={styles.title}>{t`Закрыт`}</span>
                            <div className={styles.data}>
                                {getFormattedDate(item.closed_at, {format: dateFormat, nullValue: '—'})}
                            </div>
                        </div>
                    </div>
                </div>
            }
            open={open}
            handleClose={handleClose}
            size="md"
        >
            <ModalBodyWrapper>
                {!isHeartbeat && (
                    <div className={styles.actions}>
                        {
                            item.rule
                                ? (
                                    <LinkButton
                                        href={AppUrls.SERVER_DETAILS.replace(':serverId', serverUuid)}
                                        className={styles.link}
                                    >
                                        {t`Перейти к правилам сервера`}
                                    </LinkButton>
                                )
                                : (
                                    <div className={styles.ruleRemoved}>
                                        {t`Правило удалено — показан снимок условия на момент открытия`}
                                    </div>
                                )
                        }
                    </div>
                )}

                <div className={styles.table}>
                    <div className={cn(styles.tableHeader, isTranslate() ? styles.enHeader : styles.ruHeader)}>
                        <div className={styles.headerCell}>{t`Показатель`}</div>
                        <div className={styles.headerCell}>{t`Значение`}</div>
                    </div>
                    {items.map((row) => (
                        <div key={row.label} className={styles.tableRow}>
                            <div className={styles.label}>{row.label}</div>
                            <div className={styles.value}>{row.value}</div>
                        </div>
                    ))}
                </div>
            </ModalBodyWrapper>
        </MainModal>
    );
};

export default ServerIncidentModal;
